import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScanLine, Users, Box, Trash2, Send, AlertCircle, X, Plus, FileText, Layers, ShieldAlert, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { SearchSelect } from '@/components/po/SearchSelect';
import { toast } from 'sonner';
import { validateBundleFormat } from '@/utils/validation';
import StaffIdInput from '@/components/StaffIdInput';
import { verifyAdmin } from '@/services/authStepUp';
import {
  getPrepastedDocumentsDoc,
  getPrepastedSorts,
  validatePrepastedUsnDoc,
  submitPrepastedPackingDoc,
} from '@/services/fobSectionApi';

const PrepastedAssortmentPacking = () => {
  const navigate = useNavigate();

  const [staffList, setStaffList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [sortsData, setSortsData] = useState(null); // {document_barcode, sorts:[...]}
  const [selectedSortNo, setSelectedSortNo] = useState('');

  const [usnCode, setUsnCode] = useState('');
  const [session, setSession] = useState([]); // [{usn_code, sort_no, fg_barcode, is_excess, is_damage}]
  const [outputBundle, setOutputBundle] = useState('');
  const [damageMode, setDamageMode] = useState(false);

  // Excess approval (per-sort, admin credentials)
  const [excessApprovedSorts, setExcessApprovedSorts] = useState(new Set());
  const [excessApproval, setExcessApproval] = useState(null); // {admin_email, admin_name, reason}
  const [showExcessDialog, setShowExcessDialog] = useState(false);
  const [pendingExcessScan, setPendingExcessScan] = useState('');
  const [ovEmail, setOvEmail] = useState('');
  const [ovPassword, setOvPassword] = useState('');
  const [ovReason, setOvReason] = useState('');
  const [ovVerifying, setOvVerifying] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);

  const staffInputRef = useRef(null);
  const usnInputRef = useRef(null);
  const outputBundleRef = useRef(null);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    setIsLoadingDocs(true);
    try {
      const data = await getPrepastedDocumentsDoc();
      setDocuments(data.documents || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load documents');
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const fetchSorts = async (entryId) => {
    try {
      const data = await getPrepastedSorts(entryId);
      setSortsData(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load sort nos');
      setSortsData(null);
    }
  };

  const hasStaff = staffList.length > 0;
  const sorts = useMemo(() => sortsData?.sorts || [], [sortsData]);

  const currentSort = useMemo(
    () => sorts.find((s) => String(s.sort_no) === String(selectedSortNo)),
    [sorts, selectedSortNo]
  );

  const sessionCountFor = (sortNo) =>
    session.filter((s) => String(s.sort_no) === String(sortNo)).length;

  const remainingForCurrent = currentSort
    ? Math.max(0, currentSort.remaining - sessionCountFor(selectedSortNo))
    : 0;

  const sortApproved = (sortNo) => excessApprovedSorts.has(String(sortNo));

  // ---------- Staff ----------
  const handleStaffValidated = (result) => {
    if (!result.valid) return;
    if (staffList.some((s) => s.staff_id === result.staffId)) {
      toast.error('This staff is already added');
      return;
    }
    setStaffList((prev) => [...prev, { staff_id: result.staffId, staff_name: result.staffName || '' }]);
    setTimeout(() => staffInputRef.current?.focus(), 100);
  };

  const handleRemoveStaff = (staffId) => {
    if (session.length > 0) {
      toast.error('Clear the current session before changing staff');
      return;
    }
    setStaffList((prev) => prev.filter((s) => s.staff_id !== staffId));
  };

  // ---------- Document / Sort selection ----------
  const handleSelectDocument = (entryId) => {
    if (session.length > 0) {
      toast.error('Finish or clear the current session before switching document');
      return;
    }
    setSelectedEntryId(entryId);
    setSelectedSortNo('');
    setSortsData(null);
    setExcessApprovedSorts(new Set());
    setExcessApproval(null);
    if (entryId) fetchSorts(entryId);
  };

  const handleSelectSort = (sortNo) => {
    setSelectedSortNo(sortNo);
    setTimeout(() => usnInputRef.current?.focus(), 100);
  };

  // ---------- USN scanning ----------
  const doValidateAndAdd = async (code, allowExcess) => {
    setIsProcessing(true);
    try {
      const res = await validatePrepastedUsnDoc({
        usnCode: code,
        entryId: selectedEntryId,
        sortNo: selectedSortNo,
        sessionCount: sessionCountFor(selectedSortNo),
        allowExcess,
      });
      setSession((prev) => [
        { usn_code: code, sort_no: selectedSortNo, fg_barcode: res.fg_barcode || '', is_excess: !!res.is_excess, is_damage: damageMode },
        ...prev,
      ]);
      setUsnCode('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const isExcessGate = error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'EXCESS_REQUIRES_APPROVAL';
      if (isExcessGate) {
        setPendingExcessScan(code);
        setShowExcessDialog(true);
      } else {
        const msg = typeof detail === 'string' ? detail : detail?.message;
        toast.error(msg || 'Invalid USN');
        setUsnCode('');
      }
    } finally {
      setIsProcessing(false);
      setTimeout(() => usnInputRef.current?.focus(), 50);
    }
  };

  const handleUsnScan = async (e) => {
    e.preventDefault();
    const scanned = usnCode.trim();
    if (!scanned) { usnInputRef.current?.focus(); return; }
    if (!selectedEntryId || !selectedSortNo) {
      toast.error('Pick a document and sort no first');
      return;
    }
    if (session.some((s) => s.usn_code === scanned)) {
      toast.error('This USN is already scanned in this session');
      setUsnCode('');
      usnInputRef.current?.focus();
      return;
    }
    await doValidateAndAdd(scanned, sortApproved(selectedSortNo));
  };

  const handleApproveExcess = async () => {
    if (!ovEmail.trim() || !ovPassword) { toast.error('Enter admin email and password'); return; }
    if (!ovReason.trim()) { toast.error('A reason is required'); return; }
    setOvVerifying(true);
    try {
      const res = await verifyAdmin(ovEmail.trim(), ovPassword);
      const approval = { admin_email: res.admin_email, admin_name: res.admin_name, reason: ovReason.trim() };
      setExcessApproval(approval);
      setExcessApprovedSorts((prev) => new Set(prev).add(String(selectedSortNo)));
      setShowExcessDialog(false);
      setOvEmail(''); setOvPassword(''); setOvReason('');
      toast.success(`Excess approved by ${res.admin_name || res.admin_email}`);
      const toAdd = pendingExcessScan;
      setPendingExcessScan('');
      if (toAdd) await doValidateAndAdd(toAdd, true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin verification failed');
    } finally {
      setOvVerifying(false);
    }
  };

  const handleRemoveUsn = (usn) => {
    setSession((prev) => prev.filter((s) => s.usn_code !== usn));
    setTimeout(() => usnInputRef.current?.focus(), 50);
  };

  const handleClearSession = () => {
    if (session.length === 0) return;
    if (!window.confirm(`Clear all ${session.length} scanned USNs?`)) return;
    setSession([]);
    setOutputBundle('');
  };

  // ---------- Submit ----------
  const handleOutputBundleScan = async (e) => {
    e.preventDefault();
    const scannedBundle = outputBundle.trim();
    if (!scannedBundle) { outputBundleRef.current?.focus(); return; }
    const formatValidation = validateBundleFormat(scannedBundle);
    if (!formatValidation.valid) {
      toast.error(formatValidation.error);
      setOutputBundle('');
      return;
    }
    if (session.length === 0) {
      toast.error('Scan at least one USN before packing');
      return;
    }

    setIsSubmitting(true);
    try {
      const scans = session.map((s) => ({ usn_code: s.usn_code, sort_no: s.sort_no, is_damage: !!s.is_damage }));
      const hasExcess = session.some((s) => s.is_excess);
      const result = await submitPrepastedPackingDoc(
        staffList,
        scannedBundle,
        selectedEntryId,
        scans,
        hasExcess ? excessApproval : null
      );
      const breakdown = (result.staff_breakdown || [])
        .map((s) => `${s.staff_name || s.staff_id}: ${s.attributed_qty}`)
        .join(', ');
      toast.success(
        <div>
          <p className="font-semibold">Tagged &amp; Packed!</p>
          <p className="text-sm">{session.length} pieces → {scannedBundle}</p>
          {result.excess_qty > 0 && <p className="text-xs mt-1 text-amber-700">Incl. {result.excess_qty} approved excess</p>}
          {breakdown && <p className="text-xs mt-1">Split: {breakdown}</p>}
        </div>,
        { duration: 5000 }
      );
      setSession([]);
      setOutputBundle('');
      setSelectedSortNo('');
      setExcessApprovedSorts(new Set());
      setExcessApproval(null);
      if (selectedEntryId) await fetchSorts(selectedEntryId);
      await fetchDocuments();
      setTimeout(() => usnInputRef.current?.focus(), 100);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to pack');
      setOutputBundle('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedDoc = documents.find((d) => d.entry_id === selectedEntryId);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/fob')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Assortment Packing</h1>
              <p className="text-sm text-[#E8DCC8]">FOB — Pre-pasted (document → sort)</p>
            </div>
          </div>
          <span className="text-xs bg-emerald-600 px-2 py-1 rounded-full font-medium">Pre-pasted</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Staff */}
        <Card className={`border-2 ${hasStaff ? 'border-green-500 bg-green-50/30' : 'border-[#E5DFD0]'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${hasStaff ? 'bg-green-500' : 'bg-[#2D5F4F]'}`}>1</div>
              <Users className="w-5 h-5 text-[#2D5F4F]" />
              Staff (add one or more)
            </CardTitle>
            <CardDescription>Scan each staff ID. Quantity is split equally for payroll.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <StaffIdInput inputRef={staffInputRef} onValidated={handleStaffValidated} autoFocus={true} label="" />
            {hasStaff && (
              <div className="flex flex-wrap gap-2" data-testid="staff-chips">
                {staffList.map((s) => (
                  <div key={s.staff_id} className="flex items-center gap-2 bg-[#2D5F4F] text-white px-3 py-1.5 rounded-lg" data-testid={`staff-chip-${s.staff_id}`}>
                    <span className="font-mono text-sm">{s.staff_id}</span>
                    {s.staff_name && <span className="text-xs text-[#E8DCC8]">{s.staff_name}</span>}
                    <button onClick={() => handleRemoveStaff(s.staff_id)} className="hover:text-red-300" data-testid={`remove-staff-${s.staff_id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Document → Sort cascade (document-level, mixing bundles) */}
        {hasStaff && (
          <Card className="border-2 border-[#2D5F4F]">
            <CardHeader className="pb-3 bg-[#2D5F4F]/5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-[#2D5F4F]">2</div>
                <FileText className="w-5 h-5 text-[#2D5F4F]" />
                Choose Document → Sort No
              </CardTitle>
              <CardDescription>Pick the document and the sort no — scan against the whole-document total (bundles are mixed).</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <div>
                <Label className="text-xs text-[#5A5F6E]">Document</Label>
                <div className="mt-1">
                  <SearchSelect
                    value={selectedEntryId}
                    onChange={handleSelectDocument}
                    disabled={isLoadingDocs}
                    loading={isLoadingDocs}
                    placeholder={isLoadingDocs ? 'Loading...' : 'Select document'}
                    searchPlaceholder="Search document no or vendor…"
                    empty="No document found"
                    testId="prepasted-document-select"
                    options={documents.map((d) => ({
                      value: d.entry_id,
                      label: d.document_barcode,
                      sub: `${d.vendor_name || 'N/A'} • tagged ${d.scanned_qty}/${d.entered_qty}`,
                    }))}
                  />
                </div>
                {!isLoadingDocs && documents.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1" data-testid="prepasted-no-docs">
                    No pre-pasted documents ready. Enter sort nos + qty in Sort No → USN Tagging first.
                  </p>
                )}
              </div>

              {selectedEntryId && (
                <div>
                  <Label className="text-xs text-[#5A5F6E]">Sort No (document total)</Label>
                  <Select value={selectedSortNo} onValueChange={handleSelectSort}>
                    <SelectTrigger className="mt-1" data-testid="prepasted-sort-select">
                      <SelectValue placeholder="Select sort no" />
                    </SelectTrigger>
                    <SelectContent>
                      {sorts.filter((s) => {
                        // Show only sort nos that still have pieces to pack.
                        // Keep excess-approved sorts (operator may over-scan) and
                        // the currently-selected sort so the Select stays valid.
                        const r = Math.max(0, s.remaining - sessionCountFor(s.sort_no));
                        return r > 0 || sortApproved(s.sort_no) || String(s.sort_no) === String(selectedSortNo);
                      }).map((s) => {
                        const rem = Math.max(0, s.remaining - sessionCountFor(s.sort_no));
                        const dn = [s.design_name, s.shade].filter(Boolean).join(' / ');
                        return (
                          <SelectItem key={s.sort_no} value={String(s.sort_no)}>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {s.sort_no}
                                {dn ? <span className="text-[#2D5F4F] text-xs"> — {dn}</span> : null}
                                {!dn && s.fg_barcode ? <span className="text-purple-600 text-xs"> ({s.fg_barcode})</span> : null}
                              </span>
                              <span className="text-xs text-gray-500">
                                remaining {rem} of {s.entered_qty}{sortApproved(s.sort_no) ? ' • excess approved' : ''}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Scan USNs */}
        {hasStaff && selectedEntryId && selectedSortNo && (
          <Card className="border-2 border-[#2D5F4F]">
            <CardHeader className="pb-3 bg-[#2D5F4F]/5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-[#2D5F4F]">3</div>
                <ScanLine className="w-5 h-5 text-[#2D5F4F]" />
                Scan Pre-pasted USNs
              </CardTitle>
              <CardDescription>
                Sort <strong>{selectedSortNo}</strong> on <strong className="font-mono">{sortsData?.document_barcode}</strong> —{' '}
                {sortApproved(selectedSortNo)
                  ? <span className="text-amber-700 font-semibold">excess approved (scan freely)</span>
                  : <>{remainingForCurrent} remaining</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Damage Mode toggle — while ON, every scanned piece is flagged damage */}
              <div
                className={`flex items-center justify-between gap-3 mb-3 rounded-lg border p-3 ${damageMode ? 'border-red-400 bg-red-50' : 'border-[#E5DFD0] bg-[#FBFAF5]'}`}
                data-testid="damage-mode-row"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-5 h-5 ${damageMode ? 'text-red-600' : 'text-[#8A8F9E]'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${damageMode ? 'text-red-700' : 'text-[#1A1F2E]'}`}>
                      Damage Mode {damageMode ? 'ON' : 'OFF'}
                    </p>
                    <p className="text-xs text-[#5A5F6E]">
                      {damageMode
                        ? 'Every piece you scan now is recorded as DAMAGE (still packed & counted).'
                        : 'Turn on to mark scanned pieces as damage for supplier return.'}
                    </p>
                  </div>
                </div>
                <Switch checked={damageMode} onCheckedChange={setDamageMode} data-testid="damage-mode-toggle" />
              </div>
              <form onSubmit={handleUsnScan} className="flex gap-3">
                <Input
                  ref={usnInputRef}
                  value={usnCode}
                  onChange={(e) => setUsnCode(e.target.value)}
                  placeholder={damageMode ? 'Scan DAMAGE piece USN...' : 'Scan pre-printed USN sticker...'}
                  className={`flex-1 text-lg h-14 border-2 font-mono ${damageMode ? 'border-red-400' : 'border-[#2D5F4F]'}`}
                  disabled={isProcessing || isSubmitting}
                  data-testid="prepasted-usn-input"
                  autoComplete="off"
                />
                <Button type="submit" className={`h-14 px-8 text-lg ${damageMode ? 'bg-red-600 hover:bg-red-700' : 'bg-[#2D5F4F] hover:bg-[#234A3D]'}`} disabled={isProcessing || isSubmitting} data-testid="prepasted-scan-usn-btn">
                  {isProcessing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Plus className="w-5 h-5 mr-2" />Add</>
                  )}
                </Button>
              </form>
              {remainingForCurrent <= 0 && !sortApproved(selectedSortNo) && (
                <p className="text-xs text-amber-700 mt-2" data-testid="prepasted-excess-hint">
                  Entered qty reached — scanning another piece will ask for admin approval (excess).
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Session summary */}
        {hasStaff && (
          <Card className="border-2 border-[#E5DFD0] bg-gradient-to-r from-[#2D5F4F]/5 to-transparent">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E] mb-1">Pieces Scanned</p>
                  <p className="text-5xl font-bold text-[#2D5F4F]" data-testid="prepasted-usn-count">{session.length}</p>
                  {session.length > 0 && (
                    <p className="text-sm text-[#5A5F6E] mt-1">
                      Split across {staffList.length} staff (~{Math.floor(session.length / staffList.length)} each)
                    </p>
                  )}
                </div>
                {session.length > 0 && (
                  <Button variant="outline" onClick={handleClearSession} className="h-12 px-6 border-red-300 text-red-600 hover:bg-red-50" data-testid="prepasted-clear-session-btn" disabled={isSubmitting}>
                    <Trash2 className="w-5 h-5 mr-2" />Clear All
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scanned list */}
        {hasStaff && session.length > 0 && (
          <Card className="border-2 border-[#E5DFD0]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Layers className="w-5 h-5 text-[#5A5F6E]" />Scanned Pieces
              </CardTitle>
              <CardDescription>{session.length} pieces ready to pack</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {session.map((s, index) => (
                  <div key={s.usn_code} className="flex items-center justify-between p-3 bg-[#F5F1E8] rounded-lg" data-testid={`prepasted-usn-item-${index}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <span className="text-sm text-[#5A5F6E] w-8">#{session.length - index}</span>
                      <div className="min-w-0">
                        <p className="font-mono font-semibold text-[#1A1F2E] truncate">
                          {s.usn_code}
                          {s.is_excess && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">excess</span>}
                          {s.is_damage && <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold" data-testid={`prepasted-damage-badge-${index}`}>DAMAGE</span>}
                        </p>
                        <p className="text-xs text-[#5A5F6E] truncate">Sort {s.sort_no}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveUsn(s.usn_code)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`prepasted-remove-usn-${index}`} disabled={isSubmitting}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Output bundle */}
        {hasStaff && session.length > 0 && (
          <Card className="border-2 border-orange-400 bg-orange-50/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-orange-500">4</div>
                <Box className="w-5 h-5 text-orange-600" />
                Scan NEW Output Bundle to Pack
              </CardTitle>
              <CardDescription>Scan the new bundle QR to tag &amp; pack all {session.length} pieces</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleOutputBundleScan} className="flex gap-3">
                <Input
                  ref={outputBundleRef}
                  value={outputBundle}
                  onChange={(e) => setOutputBundle(e.target.value)}
                  placeholder="Scan new Bundle QR code..."
                  className="flex-1 text-lg h-14 border-2 border-orange-400 font-mono bg-white"
                  disabled={isSubmitting}
                  data-testid="prepasted-output-bundle-input"
                  autoComplete="off"
                />
                <Button type="submit" className="h-14 px-8 bg-orange-500 hover:bg-orange-600 text-lg" disabled={isSubmitting} data-testid="prepasted-complete-btn">
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Complete
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="border border-[#E5DFD0] bg-blue-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">How Pre-pasted Assortment Packing Works:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Add all staff working this bundle</li>
                  <li>Pick the document, then the sort no (whole-document total — bundles are mixed)</li>
                  <li>Scan the pre-printed USN stickers (extra scans past the entered qty need admin approval)</li>
                  <li>Scan a NEW output bundle QR to tag &amp; pack them all in one step</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Excess approval dialog (admin credentials) */}
      <Dialog open={showExcessDialog} onOpenChange={(o) => { if (!o) { setShowExcessDialog(false); setPendingExcessScan(''); } }}>
        <DialogContent className="max-w-md" data-testid="prepasted-excess-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Admin Approval — Excess Scan
            </DialogTitle>
            <DialogDescription>
              You&apos;re scanning more pieces than the entered qty for Sort No <strong>{selectedSortNo}</strong>.
              An administrator must approve the excess. Once approved, you can keep scanning this sort.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Admin Email</Label>
              <Input value={ovEmail} onChange={(e) => setOvEmail(e.target.value)} placeholder="admin@company.com" data-testid="prepasted-excess-email" autoComplete="off" />
            </div>
            <div>
              <Label className="text-xs">Admin Password</Label>
              <Input type="password" value={ovPassword} onChange={(e) => setOvPassword(e.target.value)} placeholder="••••••••" data-testid="prepasted-excess-password" autoComplete="new-password" />
            </div>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Input value={ovReason} onChange={(e) => setOvReason(e.target.value)} placeholder="e.g. vendor sent extra pieces" data-testid="prepasted-excess-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowExcessDialog(false); setPendingExcessScan(''); setUsnCode(''); }} data-testid="prepasted-excess-cancel">
              Cancel
            </Button>
            <Button onClick={handleApproveExcess} disabled={ovVerifying} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="prepasted-excess-confirm">
              {ovVerifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Approve excess
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrepastedAssortmentPacking;
